import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useUrlFilters from "hooks/useUrlFilters";
import {
  Card, Tabs, Tag, Button, Select, Modal, message, Alert,
  Drawer, Descriptions, Form, Input, Switch,
} from "antd";
import Table from 'components/Common/Table';
import { SyncOutlined, CloudDownloadOutlined, EyeOutlined, PlusOutlined, EditOutlined } from "@ant-design/icons";

import {
  getGatewayPlans, getGatewaySubscriptions, syncGatewayPlans, errorMessage,
  getGatewayOffers, syncGatewayOffers, createGatewayOffer, updateGatewayOffer,
  getProducts,
} from "./billingApi";

const PROVIDERS = [
  { value: "RAZORPAY", label: "Razorpay" },
  { value: "CASHFREE", label: "Cashfree" },
];

const GW_STATUS_COLORS = {
  active: "green", authenticated: "blue", created: "default", pending: "orange",
  halted: "red", cancelled: "default", completed: "cyan", paused: "purple",
};
const TESON_STATUS_COLORS = {
  trial: "blue", pending: "orange", active: "green", paused: "purple",
  grace: "volcano", cancelled: "default", expired: "default", failed: "red", incomplete: "default",
};

// Active tab and per-tab provider filters live in the URL (`tab` is also the
// deep-link param used by the Subscriptions drawer).
const URL_FILTERS = {
  tab: { type: "string", default: "plans" },
  planProvider: { type: "string", key: "plan_provider" },
  subProvider: { type: "string", key: "sub_provider" },
  offerProvider: { type: "string", key: "offer_provider" },
};

const BillingGateway = () => {
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const urlSetter = (name) => (value) => setFilters({ [name]: value });
  const activeTab = filters.tab;
  const setActiveTab = urlSetter("tab");

  const [gwPlans, setGwPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const planProvider = filters.planProvider || undefined;
  const setPlanProvider = urlSetter("planProvider");

  const [gwSubs, setGwSubs] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const subProvider = filters.subProvider || undefined;
  const setSubProvider = urlSetter("subProvider");

  const [gwOffers, setGwOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const offerProvider = filters.offerProvider || undefined;
  const setOfferProvider = urlSetter("offerProvider");
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [offerSaving, setOfferSaving] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null); // null = create, record = edit
  const [offerForm] = Form.useForm();

  const [products, setProducts] = useState([]);
  const productName = useCallback(
    (id) => products.find((p) => p.id === id)?.name || (id ? String(id) : "—"),
    [products],
  );

  // Sync modal
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncKind, setSyncKind] = useState("plans"); // "plans" | "offers"
  const [syncProvider, setSyncProvider] = useState("RAZORPAY");
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Detail drawer: { type: "plan" | "sub", record }
  const [detail, setDetail] = useState(null);
  const [searchParams] = useSearchParams();
  const openDetail = useCallback((type, record) => setDetail({ type, record }), []);

  const loadPlans = async (provider) => {
    setPlansLoading(true);
    try {
      const res = await getGatewayPlans(provider ? { provider } : {});
      const data = res.data?.results || res.data || [];
      setGwPlans(Array.isArray(data) ? data : []);
    } catch (err) {
      message.error(errorMessage(err, "Could not load gateway plans."));
    } finally {
      setPlansLoading(false);
    }
  };

  const loadSubs = async (provider) => {
    setSubsLoading(true);
    try {
      const res = await getGatewaySubscriptions(provider ? { provider } : {});
      const data = res.data?.results || res.data || [];
      setGwSubs(Array.isArray(data) ? data : []);
    } catch (err) {
      message.error(errorMessage(err, "Could not load gateway subscriptions."));
    } finally {
      setSubsLoading(false);
    }
  };

  const loadOffers = async (provider) => {
    setOffersLoading(true);
    try {
      const res = await getGatewayOffers(provider ? { provider } : {});
      const data = res.data?.results || res.data || [];
      setGwOffers(Array.isArray(data) ? data : []);
    } catch (err) {
      message.error(errorMessage(err, "Could not load gateway offers."));
    } finally {
      setOffersLoading(false);
    }
  };

  useEffect(() => { loadPlans(planProvider); }, [planProvider]);
  useEffect(() => { loadSubs(subProvider); }, [subProvider]);
  useEffect(() => { loadOffers(offerProvider); }, [offerProvider]);

  useEffect(() => {
    getProducts()
      .then((res) => {
        const data = res.data?.results || res.data || [];
        setProducts(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
  }, []);

  // Deep link (e.g. from the Subscriptions drawer's "Provider sub id"):
  // /billing/gateway?tab=subscriptions&sub=<provider_subscription_id>
  useEffect(() => {
    const subId = searchParams.get("sub");
    const planId = searchParams.get("plan");
    if (subId) {
      getGatewaySubscriptions({ search: subId }).then((res) => {
        const data = res.data?.results || res.data || [];
        const rec = (Array.isArray(data) ? data : []).find(
          (g) => g.provider_subscription_id === subId) || data[0];
        if (rec) { setActiveTab("subscriptions"); openDetail("sub", rec); }
      }).catch(() => {});
    } else if (planId) {
      getGatewayPlans({ search: planId }).then((res) => {
        const data = res.data?.results || res.data || [];
        const rec = (Array.isArray(data) ? data : []).find(
          (g) => g.provider_plan_id === planId) || data[0];
        if (rec) { setActiveTab("plans"); openDetail("plan", rec); }
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const fn = syncKind === "offers" ? syncGatewayOffers : syncGatewayPlans;
      const res = await fn({ provider: syncProvider });
      setSyncResult(res.data);
      message.success(`Synced ${syncProvider} ${syncKind}: ${res.data.created} created, ${res.data.updated} updated`);
      if (syncKind === "offers") loadOffers(offerProvider);
      else loadPlans(planProvider);
    } catch (err) {
      message.error(errorMessage(err, "Sync failed."));
    } finally {
      setSyncing(false);
    }
  };

  const openCreateOffer = () => {
    setEditingOffer(null);
    offerForm.resetFields();
    setOfferModalOpen(true);
  };

  const openEditOffer = (record) => {
    setEditingOffer(record);
    offerForm.setFieldsValue({
      provider: record.provider,
      provider_offer_id: record.provider_offer_id,
      name: record.name,
      offer_type: record.offer_type,
      description: record.description,
      product: record.product ?? undefined,
      product_discount_coupon_id: record.product_discount_coupon_id ?? undefined,
      is_active: record.is_active,
    });
    setOfferModalOpen(true);
  };

  const handleSaveOffer = async () => {
    let values;
    try { values = await offerForm.validateFields(); } catch { return; }
    const payload = {
      ...values,
      product: values.product || null,
      product_discount_coupon_id: values.product_discount_coupon_id || null,
      is_active: values.is_active ?? true,
    };
    setOfferSaving(true);
    try {
      if (editingOffer) {
        await updateGatewayOffer(editingOffer.id, payload);
        message.success("Gateway offer updated.");
      } else {
        await createGatewayOffer(payload);
        message.success("Gateway offer added.");
      }
      setOfferModalOpen(false);
      setEditingOffer(null);
      loadOffers(offerProvider);
    } catch (err) {
      message.error(errorMessage(err, "Could not save gateway offer."));
    } finally {
      setOfferSaving(false);
    }
  };

  const planColumns = [
    { title: "Provider", dataIndex: "provider", key: "provider", render: (v) => <Tag color="blue">{v}</Tag> },
    { title: "Name", dataIndex: "name", key: "name", render: (v) => v || "—" },
    { title: "Provider Plan ID", dataIndex: "provider_plan_id", key: "provider_plan_id" },
    {
      title: "Amount", key: "amount",
      render: (_, r) => (r.amount != null ? `${r.currency} ${r.amount}` : "—"),
    },
    {
      title: "Period", key: "period",
      render: (_, r) => (r.period ? (r.interval > 1 ? `Every ${r.interval} ${r.period}` : r.period) : "—"),
    },
    { title: "Active", dataIndex: "is_active", key: "is_active", render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>) },
    { title: "Sync", dataIndex: "sync_status", key: "sync_status", render: (v) => <Tag>{v}</Tag> },
    {
      title: "Last Synced", dataIndex: "last_synced_at", key: "last_synced_at",
      render: (v) => (v ? new Date(v).toLocaleString() : "—"),
    },
    {
      title: "", key: "actions",
      render: (_, r) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail("plan", r)}>View</Button>
      ),
    },
  ];

  const subColumns = [
    { title: "Customer", dataIndex: "customer_email", key: "customer_email", render: (v) => v || "—" },
    { title: "Plan", dataIndex: "plan_name", key: "plan_name", render: (v) => v || "—" },
    { title: "Product", dataIndex: "product_name", key: "product_name", render: (v) => v || "—" },
    { title: "Provider", dataIndex: "provider", key: "provider", render: (v) => <Tag color="blue">{v}</Tag> },
    {
      title: "Gateway Sub ID", dataIndex: "provider_subscription_id", key: "provider_subscription_id",
      render: (v) => v || "—",
    },
    {
      title: "Gateway Status", dataIndex: "status", key: "status",
      render: (v) => (v ? <Tag color={GW_STATUS_COLORS[v] || "default"}>{v}</Tag> : "—"),
    },
    {
      title: "Teson Status", dataIndex: "teson_status", key: "teson_status",
      render: (v) => (v ? <Tag color={TESON_STATUS_COLORS[v] || "default"}>{v}</Tag> : "—"),
    },
    {
      title: "Current", dataIndex: "is_current", key: "is_current",
      render: (v) => (v ? <Tag color="green">Current</Tag> : <Tag>History</Tag>),
    },
    { title: "Reason", dataIndex: "reason", key: "reason", render: (v) => <Tag>{v}</Tag> },
    {
      title: "", key: "actions",
      render: (_, r) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail("sub", r)}>View</Button>
      ),
    },
  ];

  const offerColumns = [
    { title: "Provider", dataIndex: "provider", key: "provider", render: (v) => <Tag color="blue">{v}</Tag> },
    { title: "Name", dataIndex: "name", key: "name", render: (v) => v || "—" },
    { title: "Provider Offer ID", dataIndex: "provider_offer_id", key: "provider_offer_id" },
    { title: "Type", dataIndex: "offer_type", key: "offer_type", render: (v) => (v ? <Tag>{v}</Tag> : "—") },
    { title: "Product", dataIndex: "product", key: "product", render: (v) => productName(v) },
    {
      title: "Coupon ID", dataIndex: "product_discount_coupon_id", key: "product_discount_coupon_id",
      render: (v) => (v ? <Tag color="gold">{v}</Tag> : "—"),
    },
    { title: "Active", dataIndex: "is_active", key: "is_active", render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>) },
    {
      title: "Last Synced", dataIndex: "last_synced_at", key: "last_synced_at",
      render: (v) => (v ? new Date(v).toLocaleString() : "—"),
    },
    {
      title: "", key: "actions",
      render: (_, r) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail("offer", r)}>View</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditOffer(r)}>Edit</Button>
        </div>
      ),
    },
  ];

  const tabItems = [
    {
      key: "plans",
      label: "Gateway Plans",
      children: (
        <div>
          <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between" }}>
            <Select
              placeholder="Filter by provider" allowClear style={{ width: 200 }}
              value={planProvider} onChange={setPlanProvider} options={PROVIDERS}
            />
            <Button type="primary" icon={<CloudDownloadOutlined />} onClick={() => { setSyncKind("plans"); setSyncResult(null); setSyncOpen(true); }}>
              Sync from Gateway
            </Button>
          </div>
          <Table dataSource={gwPlans} columns={planColumns} rowKey="id" loading={plansLoading} size="small" pagination={false} />
        </div>
      ),
    },
    {
      key: "subscriptions",
      label: "Gateway Subscriptions",
      children: (
        <div>
          <div style={{ marginBottom: 12 }}>
            <Select
              placeholder="Filter by provider" allowClear style={{ width: 200 }}
              value={subProvider} onChange={setSubProvider} options={PROVIDERS}
            />
          </div>
          <Table dataSource={gwSubs} columns={subColumns} rowKey="id" loading={subsLoading} size="small" pagination={{ defaultPageSize: 20, showSizeChanger: true }} />
        </div>
      ),
    },
    {
      key: "offers",
      label: "Gateway Offers",
      children: (
        <div>
          <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between" }}>
            <Select
              placeholder="Filter by provider" allowClear style={{ width: 200 }}
              value={offerProvider} onChange={setOfferProvider} options={PROVIDERS}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <Button icon={<PlusOutlined />} onClick={openCreateOffer}>
                Add offer
              </Button>
              <Button type="primary" icon={<CloudDownloadOutlined />} onClick={() => { setSyncKind("offers"); setSyncResult(null); setSyncOpen(true); }}>
                Sync from Gateway
              </Button>
            </div>
          </div>
          <Alert type="info" showIcon style={{ marginBottom: 8 }}
            message="Razorpay has no list-offers API. Add the offer id from the Razorpay dashboard here; it is then applied to the subscription at the gateway during checkout." />
          <Table dataSource={gwOffers} columns={offerColumns} rowKey="id" loading={offersLoading} size="small" pagination={false} />
        </div>
      ),
    },
  ];

  return (
    <Card title="Gateway" bordered={false}>
      <Alert
        type="info" showIcon style={{ marginBottom: 16 }}
        message="Gateway plans and subscriptions are synced from the payment provider and are read-only here. Gateway offers are synced too, but their product and discount-coupon linkage can be edited."
      />
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />

      <Modal
        title={`Sync ${syncKind === "offers" ? "Offers" : "Plans"} from Gateway`}
        open={syncOpen}
        onCancel={() => { setSyncOpen(false); setSyncResult(null); }}
        footer={syncResult ? [
          <Button key="close" onClick={() => { setSyncOpen(false); setSyncResult(null); }}>Close</Button>,
        ] : [
          <Button key="cancel" onClick={() => setSyncOpen(false)}>Cancel</Button>,
          <Button key="sync" type="primary" loading={syncing} icon={<SyncOutlined />} onClick={handleSync}>
            Sync
          </Button>,
        ]}
      >
        {!syncResult ? (
          <div>
            <p>Pull {syncKind === "offers" ? "offers" : "plans"} from the selected gateway and update the local mirror.</p>
            <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Gateway</label>
            <Select value={syncProvider} onChange={setSyncProvider} options={PROVIDERS} style={{ width: "100%" }} />
          </div>
        ) : (
          <Alert
            type="success" showIcon
            message={`Sync complete (${syncResult.provider})`}
            description={
              <span>
                Created: <strong>{syncResult.created}</strong> · Updated: <strong>{syncResult.updated}</strong> · Total: <strong>{syncResult.total}</strong>
              </span>
            }
          />
        )}
      </Modal>

      <Modal
        title={editingOffer ? "Edit gateway offer" : "Add gateway offer"}
        open={offerModalOpen}
        onCancel={() => { setOfferModalOpen(false); setEditingOffer(null); }}
        onOk={handleSaveOffer}
        okText={editingOffer ? "Save changes" : "Add offer"}
        confirmLoading={offerSaving}
        destroyOnClose
      >
        <Form form={offerForm} layout="vertical">
          <Form.Item name="provider" label="Gateway" initialValue="RAZORPAY" rules={[{ required: true }]}>
            <Select options={PROVIDERS} />
          </Form.Item>
          <Form.Item name="provider_offer_id" label="Gateway offer id"
            rules={[{ required: true }]}
            extra="The offer id from the gateway dashboard (e.g. Razorpay offer_XXXXXXXX).">
            <Input placeholder="offer_XXXXXXXXXXXX" />
          </Form.Item>
          <Form.Item name="name" label="Name"><Input placeholder="e.g. Diwali 10% off" /></Form.Item>
          <Form.Item name="offer_type" label="Type"><Input placeholder="e.g. instant" /></Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="product" label="Product"
            extra="Restrict this offer to one product. Leave empty to apply to any product.">
            <Select
              allowClear showSearch optionFilterProp="label" placeholder="Any product"
              options={products.map((p) => ({ value: p.id, label: p.name }))}
            />
          </Form.Item>
          <Form.Item name="product_discount_coupon_id" label="Product discount coupon ID"
            extra="The external product's coupon id (e.g. a Pixelshield DiscountCoupon UUID). Multiple offers may share the same id.">
            <Input placeholder="e.g. 3f1c…-coupon-uuid" />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={detail?.type === "plan" ? "Gateway Plan"
          : detail?.type === "offer" ? "Gateway Offer" : "Gateway Subscription"}
        width={520}
        open={!!detail}
        onClose={() => setDetail(null)}
      >
        {detail?.type === "plan" && (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Provider"><Tag color="blue">{detail.record.provider}</Tag></Descriptions.Item>
            <Descriptions.Item label="Provider Plan ID">{detail.record.provider_plan_id || "—"}</Descriptions.Item>
            <Descriptions.Item label="Name">{detail.record.name || "—"}</Descriptions.Item>
            <Descriptions.Item label="Amount">{detail.record.amount != null ? `${detail.record.currency} ${detail.record.amount}` : "—"}</Descriptions.Item>
            <Descriptions.Item label="Period">{detail.record.period || "—"}</Descriptions.Item>
            <Descriptions.Item label="Interval">{detail.record.interval ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="Active">{detail.record.is_active ? "Yes" : "No"}</Descriptions.Item>
            <Descriptions.Item label="Sync Status">{detail.record.sync_status}</Descriptions.Item>
            <Descriptions.Item label="Last Synced">{detail.record.last_synced_at ? new Date(detail.record.last_synced_at).toLocaleString() : "—"}</Descriptions.Item>
          </Descriptions>
        )}
        {detail?.type === "offer" && (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Provider"><Tag color="blue">{detail.record.provider}</Tag></Descriptions.Item>
            <Descriptions.Item label="Provider Offer ID">{detail.record.provider_offer_id || "—"}</Descriptions.Item>
            <Descriptions.Item label="Name">{detail.record.name || "—"}</Descriptions.Item>
            <Descriptions.Item label="Type">{detail.record.offer_type || "—"}</Descriptions.Item>
            <Descriptions.Item label="Description">{detail.record.description || "—"}</Descriptions.Item>
            <Descriptions.Item label="Product">{productName(detail.record.product)}</Descriptions.Item>
            <Descriptions.Item label="Coupon ID">{detail.record.product_discount_coupon_id || "—"}</Descriptions.Item>
            <Descriptions.Item label="Active">{detail.record.is_active ? "Yes" : "No"}</Descriptions.Item>
            <Descriptions.Item label="Sync Status">{detail.record.sync_status}</Descriptions.Item>
            <Descriptions.Item label="Last Synced">{detail.record.last_synced_at ? new Date(detail.record.last_synced_at).toLocaleString() : "—"}</Descriptions.Item>
          </Descriptions>
        )}
        {detail?.type === "sub" && (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Customer">{detail.record.customer_name || detail.record.customer_email || "—"}</Descriptions.Item>
            <Descriptions.Item label="Email">{detail.record.customer_email || "—"}</Descriptions.Item>
            <Descriptions.Item label="Plan">{detail.record.plan_name || "—"}</Descriptions.Item>
            <Descriptions.Item label="Product">{detail.record.product_name || "—"}</Descriptions.Item>
            <Descriptions.Item label="Provider"><Tag color="blue">{detail.record.provider}</Tag></Descriptions.Item>
            <Descriptions.Item label="Provider Sub ID">{detail.record.provider_subscription_id || "—"}</Descriptions.Item>
            <Descriptions.Item label="Provider Customer ID">{detail.record.provider_customer_id || "—"}</Descriptions.Item>
            <Descriptions.Item label="Provider Plan ID">{detail.record.provider_plan_id || "—"}</Descriptions.Item>
            <Descriptions.Item label="Gateway Status">
              {detail.record.status ? <Tag color={GW_STATUS_COLORS[detail.record.status] || "default"}>{detail.record.status}</Tag> : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Teson Status">
              {detail.record.teson_status ? <Tag color={TESON_STATUS_COLORS[detail.record.teson_status] || "default"}>{detail.record.teson_status}</Tag> : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Current">{detail.record.is_current ? "Yes" : "No"}</Descriptions.Item>
            <Descriptions.Item label="Reason">{detail.record.reason}</Descriptions.Item>
            <Descriptions.Item label="Checkout Link">
              {detail.record.short_url
                ? <a href={detail.record.short_url} target="_blank" rel="noreferrer">{detail.record.short_url}</a>
                : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Start">{detail.record.start_date ? new Date(detail.record.start_date).toLocaleString() : "—"}</Descriptions.Item>
            <Descriptions.Item label="End">{detail.record.end_date ? new Date(detail.record.end_date).toLocaleString() : "—"}</Descriptions.Item>
            <Descriptions.Item label="Created">{detail.record.created_at ? new Date(detail.record.created_at).toLocaleString() : "—"}</Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </Card>
  );
};

export default BillingGateway;
